import { useEffect, useRef, useState } from "react";
import { Lock, ChevronRight } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_fooquinha-content/artifacts/wxmcek8b_image.png";

export default function VerificationGate({ onVerified }) {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const fillRef = useRef(null);
  const labelRef = useRef(null);
  const [unlocked, setUnlocked] = useState(false);
  const [hearts, setHearts] = useState([]);

  useEffect(() => {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    const fill = fillRef.current;
    const label = labelRef.current;
    if (!track || !thumb || !fill || !label) return;

    let dragging = false;
    let startX = 0;
    let currentX = 0;
    const padding = 4;

    const getMax = () => track.clientWidth - thumb.offsetWidth - padding * 2;

    const onDown = (e) => {
      dragging = true;
      startX = (e.touches ? e.touches[0].clientX : e.clientX) - currentX;
      thumb.style.transition = "none";
      fill.style.transition = "none";
    };

    const onMove = (e) => {
      if (!dragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      let dx = clientX - startX;
      const max = getMax();
      if (dx < 0) dx = 0;
      if (dx > max) dx = max;
      currentX = dx;
      thumb.style.transform = `translateX(${dx}px)`;
      fill.style.width = `${dx + thumb.offsetWidth + padding}px`;
      label.style.opacity = `${1 - dx / max}`;
      if (dx >= max - 2) {
        success();
      }
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      const max = getMax();
      if (currentX < max - 2) {
        thumb.style.transition = "transform 0.3s ease";
        fill.style.transition = "width 0.3s ease";
        thumb.style.transform = `translateX(0px)`;
        fill.style.width = `0px`;
        label.style.opacity = "1";
        currentX = 0;
      }
    };

    let triggered = false;
    const success = () => {
      if (triggered) return;
      triggered = true;
      // burst hearts
      const burst = Array.from({ length: 10 }).map((_, i) => ({
        id: Date.now() + i,
        left: 10 + Math.random() * 80,
        delay: Math.random() * 0.3,
        emoji: ["💖", "💕", "💋", "🌸"][i % 4],
      }));
      setHearts(burst);
      setUnlocked(true);
      setTimeout(() => onVerified && onVerified(), 520);
    };

    thumb.addEventListener("mousedown", onDown);
    thumb.addEventListener("touchstart", onDown, { passive: true });
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);

    return () => {
      thumb.removeEventListener("mousedown", onDown);
      thumb.removeEventListener("touchstart", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [onVerified]);

  return (
    <div className="gate-wrap" data-testid="verification-gate">
      <div className="gate-hero" data-testid="gate-hero">
        <span className="gate-hero-tag">
          <span className="dot" />
          online agora
        </span>
        <img
          src={HERO_IMG}
          alt="Foo"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
      </div>

      <div className={`gate-card ${unlocked ? "unlock-anim" : ""}`}>
        <h1 className="gate-title" data-testid="gate-title">
          Oi nenê <span>🌸</span>
        </h1>
        <p className="gate-sub" data-testid="gate-subtitle">
          antes de entrar, preciso saber se é você mesmo...
        </p>

        <div className="slider-track" ref={trackRef} data-testid="slide-track">
          <div className="slider-fill" ref={fillRef} />
          <div className="slider-label" ref={labelRef}>
            deslize para entrar
            <span className="arrows">›››</span>
          </div>
          <div
            className="slider-thumb"
            ref={thumbRef}
            data-testid="slide-thumb"
            role="button"
            aria-label="Deslize para entrar"
          >
            {unlocked ? (
              <span style={{ fontSize: 22 }}>💖</span>
            ) : (
              <Lock size={20} strokeWidth={2.4} />
            )}
          </div>
        </div>

        <p className="gate-foot" data-testid="gate-disclaimer">
          Descubra meus segredinhos...
        </p>

        {hearts.map((h) => (
          <span
            key={h.id}
            className="heart-burst"
            style={{
              bottom: 80,
              left: `${h.left}%`,
              animationDelay: `${h.delay}s`,
            }}
          >
            {h.emoji}
          </span>
        ))}
      </div>
    </div>
  );
}
